import React from 'react';
import { MapPin, Calendar, Shield, Users, CheckCircle, ArrowRight, Award, Factory } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const FirstCuisine = () => {
  const facilities = [
    {
      name: "Central Kitchen",
      address: "15 Senoko Drive #05-10 JTC Food Hub @ Senoko Singapore 758202",
      type: "Primary Operations",
      capabilities: ["Cook-Serve Model", "Large-scale Production", "Halal Certification", "Quality Control"]
    }
  ];

  const services = [
    "Halal-certified food manufacturing",
    "Institutional catering services", 
    "Retort food production",
    "Original Equipment Manufacturing (OEM)",
    "Therapeutic diet preparation",
    "IDDSI-compliant meals",
    "Healthcare facility catering",
    "Educational institution meals"
  ];

  const certifications = [
    { name: "Halal Certification", authority: "MUIS Singapore", icon: <Shield className="h-6 w-6 text-green-500" /> },
    { name: "Basic Food Hygiene", authority: "SFA Singapore", icon: <Award className="h-6 w-6 text-blue-500" /> },
    { name: "Advanced Food Hygiene Officer", authority: "SFA Singapore", icon: <Users className="h-6 w-6 text-purple-500" /> },
    { name: "Eatsafe SG (IDDSI)", authority: "AIC Singapore", icon: <CheckCircle className="h-6 w-6 text-orange-500" /> }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <Shield className="h-12 w-12 text-green-300" />
              <span className="text-green-200 font-semibold text-lg">Halal Certified Excellence</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">First Cuisine Food Industries Pte Ltd</h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              Singapore's leading halal-certified food manufacturing company, delivering reliable and 
              high-quality food solutions since 2003.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <Factory className="h-8 w-8 text-green-500" />
                <span className="text-green-600 font-semibold text-lg">Established Excellence</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Over 20 Years of Halal Food Manufacturing
              </h2>
              
              <div className="space-y-4 text-gray-600 mb-6">
                <p>
                  First Cuisine Food Industries Pte Ltd (UEN: 200808067K) was established on 6 March 2003 
                  as a halal-certified food manufacturing company in Singapore. The company specializes in 
                  food services, retort food production, and Original Equipment Manufacturing (OEM) for 
                  institutional and commercial clients.
                </p>
                <p>
                  With a strong foundation built on quality, safety, and innovation, First Cuisine has grown 
                  to become one of Singapore's leading institutional caterers, delivering reliable and 
                  high-quality food solutions to healthcare, education, and corporate sectors.
                </p>
              </div>

              {/* Company Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Calendar className="h-4 w-4 text-green-500" />
                      <span className="font-medium text-gray-700">Established</span>
                    </div>
                    <p className="text-gray-900">6 March 2003</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Factory className="h-4 w-4 text-green-500" />
                      <span className="font-medium text-gray-700">UEN</span>
                    </div>
                    <p className="text-gray-900">200808067K</p>
                  </CardContent>
                </Card>
              </div>

              <Button className="bg-green-600 hover:bg-green-700">
                <Link to="/contact" className="flex items-center">
                  Contact First Cuisine
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_foodholdings/artifacts/yyn6rhh5_image1.png"
                alt="First Cuisine industrial kitchen equipment and facilities"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Production Facilities */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              State-of-the-Art Production Facilities
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our strategically located facilities ensure consistent quality and efficient 
              delivery across Singapore's food service industry.
            </p>
          </div>

          <div className="flex justify-center mb-12">
            <div className="w-full max-w-2xl">
              {facilities.map((facility, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex items-center justify-center space-x-3 mb-6">
                      <MapPin className="h-6 w-6 text-green-500" />
                      <h3 className="text-2xl font-semibold text-gray-900">{facility.name}</h3>
                    </div>
                    
                    <div className="mb-6 text-center">
                      <span className="text-sm font-medium text-green-600">{facility.type}</span>
                      <p className="text-sm text-gray-600 mt-2">{facility.address}</p>
                    </div>

                    <h4 className="font-medium text-gray-700 mb-4 text-center">Capabilities:</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {facility.capabilities.map((capability, cIndex) => (
                        <div key={cIndex} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span className="text-sm text-gray-600">{capability}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Production Images */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_foodholdings/artifacts/xbfo9y3h_image2.png"
                alt="Food production process in action at First Cuisine"
                className="w-full h-64 object-cover rounded-lg shadow-lg"
              />
              <h4 className="text-lg font-semibold text-gray-900 mt-4 mb-2">
                Advanced Production Process
              </h4>
              <p className="text-gray-600">
                Our skilled team operates state-of-the-art equipment to ensure consistent quality and efficiency
              </p>
            </div>

            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_foodholdings/artifacts/tie23k8s_image3.png"
                alt="Professional food service line with multiple prepared dishes"
                className="w-full h-64 object-cover rounded-lg shadow-lg"
              />
              <h4 className="text-lg font-semibold text-gray-900 mt-4 mb-2">
                Comprehensive Food Service
              </h4>
              <p className="text-gray-600">
                Wide variety of halal-certified dishes prepared to meet institutional dining standards
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Specialties */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Services */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Services</h2>
              <p className="text-gray-600 mb-8">
                Comprehensive halal food manufacturing and catering solutions designed to meet 
                the diverse needs of institutional and commercial clients across Singapore.
              </p>
              
              <div className="space-y-3">
                {services.map((service, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Therapeutic Diets Image */}
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_foodholdings/artifacts/6ki795xy_image4.png"
                alt="Therapeutic diet meal preparations showing various texture modifications"
                className="w-full h-96 object-cover rounded-lg shadow-lg mb-4"
              />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Therapeutic Diet Specialization
              </h3>
              <p className="text-gray-600">
                IDDSI-compliant therapeutic diets carefully prepared with various texture modifications 
                to meet specific nutritional and safety requirements for healthcare institutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Certifications & Standards
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence is backed by comprehensive certifications 
              and adherence to the highest food safety standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {cert.icon}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{cert.name}</h4>
                  <div className="text-sm text-gray-500">{cert.authority}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Partner with First Cuisine
          </h2>
          <p className="text-xl mb-8 text-green-100 max-w-3xl mx-auto">
            Ready to experience Singapore's leading halal food manufacturing services? 
            Contact us today to discuss your institutional catering needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-green-600 hover:bg-gray-100">
              <Link to="/contact" className="flex items-center">
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-green-600">
              <Link to="/expertise/therapeutic-diets">Explore Therapeutic Diets</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FirstCuisine;